import { useQuery } from "@tanstack/react-query"
import { useTranslation } from "react-i18next"
import { Link } from "wouter"
import ReactMarkdown from "react-markdown"
import remarkGfm from "remark-gfm"
import { ArrowLeft } from "lucide-react"
import { api } from "@/lib/api"
import { formatDate, LOCALE_MAP } from "@/lib/utils"
import type { LegalDocumentResponse } from "@fohet/caravan-booker-shared"

// Renders VOP / Privacy Policy fetched from /api/legal/:doc?lang=.
// CZ source of truth in caravan-booker-api/public/legal/*-cs.md; EN translation in *-en.md.
// PL requests fall back to EN server-side.

// `kind` is the route's semantic name (terms / privacy). The backend doc
// slug is "vop" for terms — keep the mapping in one place.
const KIND_TO_DOC = {
    terms: "vop",
    privacy: "privacy",
} as const

export const LegalPage = ({ kind }: { kind: "terms" | "privacy" }) => {
    const { t, i18n } = useTranslation()
    const doc = KIND_TO_DOC[kind]
    // Backend only has CS + EN; PL UI users see EN. The backend handles
    // the fallback, but sending "en" upfront avoids one redirect-style
    // round-trip and matches what the user will actually read.
    const lang = i18n.language === "cs" ? "cs" : "en"
    const locale = LOCALE_MAP[i18n.language] ?? "en-US"

    const { data, isLoading, error } = useQuery({
        queryKey: ["legal", doc, lang],
        queryFn: () => api.get<LegalDocumentResponse>(`/legal/${doc}?lang=${lang}`),
        staleTime: 60 * 60 * 1000,
    })

    return (
        <div className="min-h-screen bg-slate-50 py-12 px-4">
            <div className="max-w-4xl mx-auto">
                <Link
                    to="/admin/login"
                    className="inline-flex items-center gap-2 text-sm font-black text-slate-500 hover:text-slate-800 transition-colors mb-6"
                >
                    <ArrowLeft className="size-4" />
                    {t("legal.back")}
                </Link>

                <article className="bg-white rounded-[40px] border border-slate-100 shadow-xl p-8 md:p-12">
                    {isLoading && <div className="text-slate-500 font-bold">{t("legal.loading")}</div>}
                    {error && <div className="text-red-600 font-bold">{t("legal.loadError")}</div>}
                    {data && (
                        <>
                            <div className="legal-doc">
                                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                    {data.content}
                                </ReactMarkdown>
                            </div>
                            <hr className="border-slate-200 my-6" />
                            <footer className="text-xs font-bold text-slate-400 flex flex-wrap gap-x-4 gap-y-1">
                                {data.version && (
                                    <span>{t("legal.version", { version: data.version })}</span>
                                )}
                                {data.effectiveFrom && (
                                    <span>{t("legal.effectiveFrom", { date: formatDate(data.effectiveFrom, locale) })}</span>
                                )}
                            </footer>
                        </>
                    )}
                </article>
            </div>
        </div>
    )
}
